import { useEffect, useRef, useState } from "react";
import { Check, Loader2, Pencil, Pin, PinOff, Plus, X } from "lucide-react";
import {
  useCreateNote,
  useDeleteNote,
  usePatchNote,
  useRejectProposal,
  useValidateProposal,
} from "../api/queries";
import type { PanneauNote, Proposal } from "../api/types";

interface Props {
  panneauId: string;
  author: string;
  notes: PanneauNote[];
  /** Pending `note_add` proposals for this panneau. Rendered as
   *  ghost-style rows with Valider/Refuser; default `[]`. */
  pending?: Proposal[];
}

/**
 * Notes chip — replaces the visual placeholder for the Notes column.
 * Header shows a small count badge; click → popover with the list of
 * notes (pin/unpin + delete) + a textarea to add a new one.
 *
 * v1: anyone can pin (PRD wants admin-only); no TTL enforcement on
 * temporary notes.
 */
export function NotesChip({ panneauId, author, notes, pending = [] }: Props) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const createNote = useCreateNote(panneauId);
  const patchNote = usePatchNote(panneauId);
  const deleteNote = useDeleteNote(panneauId);
  const validateProp = useValidateProposal(panneauId);
  const rejectProp = useRejectProposal(panneauId);

  // Filter to note_add proposals only.
  const pendingNotes = pending.filter((p) => p.type === "note_add");
  // Combined count drives the badge so operators see "2" if 1 validated
  // + 1 pending; the ghost-styling tells them which is which on open.
  const totalCount = notes.length + pendingNotes.length;

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="h-5 w-5 rounded-full border border-dashed border-black/30 dark:border-white/30 text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark hover:border-ink dark:hover:border-white flex items-center justify-center"
          aria-label="Gérer les notes"
        >
          <Plus className="h-3 w-3" />
        </button>
        {totalCount > 0 ? (
          <span
            className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold ${
              pendingNotes.length > 0 && notes.length === 0
                ? "bg-white dark:bg-surface-dark text-ink dark:text-ink-dark border border-dashed border-ink/40 dark:border-white/40 italic"
                : "bg-ink text-white"
            }`}
            aria-label={`${totalCount} notes${pendingNotes.length > 0 ? ` dont ${pendingNotes.length} en attente` : ""}`}
            title={`${notes.length} validée(s) + ${pendingNotes.length} en attente`}
          >
            {totalCount}
          </span>
        ) : null}
      </div>
      <span className="text-xs text-muted dark:text-muted-dark">Notes</span>

      {open && (
        <div
          ref={popoverRef}
          className="absolute left-0 top-full mt-2 z-20 w-80 bg-white dark:bg-surface-dark rounded-xl border border-black/10 dark:border-white/10 shadow-lg p-2"
          role="dialog"
          aria-label="Gestion des notes"
        >
          <ul className="max-h-56 overflow-y-auto divide-y divide-black/5">
            {notes.length === 0 && pendingNotes.length === 0 && (
              <li className="text-xs text-muted dark:text-muted-dark px-2 py-3">
                Aucune note. Ajoutez-en une ci-dessous.
              </li>
            )}
            {pendingNotes.map((p) => {
              const body = String(p.payload.body ?? "");
              const isValidating =
                validateProp.isPending && validateProp.variables === p.id;
              const isRejecting =
                rejectProp.isPending && rejectProp.variables === p.id;
              return (
                <li
                  key={p.id}
                  className="px-2 py-2 flex items-start gap-2 text-sm bg-canvas/40 dark:bg-canvas-dark/40 border-l-2 border-dashed border-ink/30 dark:border-white/30"
                >
                  <div className="flex-1 min-w-0">
                    <div className="break-words whitespace-pre-wrap italic opacity-70">
                      {body}
                    </div>
                    <div className="text-xs text-muted dark:text-muted-dark mt-0.5">
                      proposition · « {p.source_quote} »
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => validateProp.mutate(p.id)}
                    disabled={isValidating || isRejecting}
                    className="text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark p-1"
                    aria-label="Valider la proposition de note"
                    title="Valider"
                  >
                    {isValidating ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Check className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => rejectProp.mutate(p.id)}
                    disabled={isValidating || isRejecting}
                    className="text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark p-1"
                    aria-label="Refuser la proposition de note"
                    title="Refuser"
                  >
                    {isRejecting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <X className="h-3.5 w-3.5" />
                    )}
                  </button>
                </li>
              );
            })}
            {notes.map((n) => (
              <NoteRow
                key={n.id}
                note={n}
                onSaveBody={(body) =>
                  patchNote.mutateAsync({ id: n.id, body })
                }
                onTogglePin={() =>
                  patchNote.mutate({
                    id: n.id,
                    is_definitive: !n.is_definitive,
                  })
                }
                onDelete={() => deleteNote.mutate(n.id)}
                pendingPatch={patchNote.isPending}
                pendingDelete={deleteNote.isPending}
              />
            ))}
          </ul>
          <NewNoteForm
            author={author}
            onCreate={async (body) =>
              createNote.mutateAsync({ author, body })
            }
            pending={createNote.isPending}
          />
        </div>
      )}
    </div>
  );
}

interface NoteRowProps {
  note: PanneauNote;
  onSaveBody: (body: string) => Promise<unknown>;
  onTogglePin: () => void;
  onDelete: () => void;
  pendingPatch: boolean;
  pendingDelete: boolean;
}

function NoteRow({
  note,
  onSaveBody,
  onTogglePin,
  onDelete,
  pendingPatch,
  pendingDelete,
}: NoteRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);

  // Reset draft when entering edit mode (in case the note got refetched).
  useEffect(() => {
    if (editing) setDraft(note.body);
  }, [editing, note.body]);

  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === note.body) {
      setEditing(false);
      return;
    }
    try {
      await onSaveBody(trimmed);
      setEditing(false);
    } catch {
      /* swallowed; v1 has no toast */
    }
  };

  return (
    <li className="px-2 py-2 flex items-start gap-2 text-sm">
      <div className="flex-1 min-w-0">
        {editing ? (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            maxLength={4000}
            className="bun-textbox w-full px-2 py-1 text-sm rounded border border-black/10 dark:border-white/10 focus:outline-none focus:ring-1 focus:ring-ink dark:focus:ring-white resize-y"
            autoFocus
            aria-label="Modifier la note"
          />
        ) : (
          <div className="break-words whitespace-pre-wrap">{note.body}</div>
        )}
        <div className="text-xs text-muted dark:text-muted-dark mt-0.5">
          {note.author}
          {note.is_definitive && (
            <span className="ml-1.5 text-ink dark:text-ink-dark">· définitive</span>
          )}
        </div>
      </div>
      {editing ? (
        <>
          <button
            type="button"
            onClick={save}
            disabled={pendingPatch}
            className="text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark p-1"
            aria-label="Enregistrer la note"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            disabled={pendingPatch}
            className="text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark p-1"
            aria-label="Annuler"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark p-1"
            aria-label="Modifier cette note"
            title="Modifier"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onTogglePin}
            disabled={pendingPatch}
            title={
              note.is_definitive
                ? "Repasser en temporaire"
                : "Marquer comme définitive"
            }
            className="text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark p-1"
            aria-label={
              note.is_definitive ? "Dépingler" : "Épingler comme définitive"
            }
          >
            {note.is_definitive ? (
              <PinOff className="h-3.5 w-3.5" />
            ) : (
              <Pin className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={pendingDelete}
            className="text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark p-1"
            aria-label="Supprimer cette note"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </>
      )}
    </li>
  );
}

interface NewNoteFormProps {
  author: string;
  onCreate: (body: string) => Promise<unknown>;
  pending: boolean;
}

function NewNoteForm({ author, onCreate, pending }: NewNoteFormProps) {
  const [body, setBody] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    try {
      await onCreate(trimmed);
      setBody("");
    } catch {
      /* swallowed; v1 has no toast */
    }
  };

  return (
    <form onSubmit={submit} className="border-t border-black/5 dark:border-white/10 mt-1 pt-2 px-1">
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={`Nouvelle note (signée ${author})…`}
        aria-label="Nouvelle note"
        rows={2}
        maxLength={4000}
        className="bun-textbox w-full px-2 py-1.5 text-sm rounded border border-black/10 dark:border-white/10 focus:outline-none focus:ring-1 focus:ring-ink dark:focus:ring-white resize-none"
      />
      <div className="flex justify-end mt-1">
        <button
          type="submit"
          disabled={pending || !body.trim()}
          className="px-3 py-1 rounded-full bg-ink text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {pending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Plus className="h-3 w-3" />
          )}
          Ajouter
        </button>
      </div>
    </form>
  );
}
